import { FormattedMessage, useIntl } from 'react-intl';
import { Platform, StyleSheet } from 'react-native';
import Animated, { useAnimatedRef, useScrollOffset } from 'react-native-reanimated';

import { FadeView, Navbar, theme } from '@sg/garnish';
import { ProductDetails } from '@sg/shared-fe-features-product-details';

import { FloatingBar, FloatingControls } from '../../../../components';
import { useIsLiquidGlassEnabled } from '../../../../hooks';
import { messages } from '../../ProductDetails.messages';
import { type BaseProduct, type ProductDetailsMappedContent } from '../../ProductDetails.types';
import { ProductNutritionData } from '../ProductNutritionData';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsReadyXS = (props: Props) => {
  const {
    baseProduct,
    content,
    hasNoSelectedLocation,
    isUnavailableProduct,
    isAddingToBag,
    navigateBack,
    navigateToLocationSearch,
    navigateToCustomization,
  } = props;

  const { formatMessage } = useIntl();
  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();

  const scrollViewRef = useAnimatedRef<Animated.ScrollView>();
  const scrollOffsetSV = useScrollOffset(scrollViewRef);

  const hasNutritionInfo = content.shouldShowNutritionInfoSection;
  const canAddToBag = !isUnavailableProduct && !hasNoSelectedLocation;
  const hasContentBelowIngredients = hasNutritionInfo || isUnavailableProduct;
  const screenTitle = content.screenTitle ?? formatMessage(messages.screenTitle); // prettier-ignore
  const ctaMessage = getCtaMessage({ canAddToBag, hasNoSelectedLocation });
  const handleCtaPress = canAddToBag ? navigateToCustomization : navigateToLocationSearch;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <FadeView show duration={theme.transitions.long} style={styles.outerContainer}>
      <ProductDetails.NavigationHeader
        title={screenTitle}
        foregroundColor={content.mobileHeaderForegroundColor}
        backgroundColor={content.mobileHeaderBackgroundColor}
        scrollOffsetSV={scrollOffsetSV}
      />

      <Animated.ScrollView ref={scrollViewRef} contentContainerStyle={styles.contentContainer}>
        <ProductDetails.Header.Small.OuterContainer
          backgroundColor={content.mobileHeaderBackgroundColor}
          backgroundImageBaseUrl={content.mobileHeaderBackgroundImage?.fields?.file?.url} // prettier-ignore
        >
          <ProductDetails.Header.Small.Container>
            <ProductDetails.Header.Small.ContentContainer>
              <ProductDetails.Title color={content.mobileHeaderForegroundColor}>
                {baseProduct.name}
              </ProductDetails.Title>

              <ProductDetails.Text color={content.mobileHeaderForegroundColor}>
                {content.subheading ?? baseProduct.description}
              </ProductDetails.Text>
            </ProductDetails.Header.Small.ContentContainer>

            <ProductDetails.Banner.Small
              productName={baseProduct.name}
              productImageUrl={baseProduct.asset.url}
            />
          </ProductDetails.Header.Small.Container>

          {content.description ? (
            <ProductDetails.Text color={content.mobileHeaderForegroundColor}>
              {content.description}
            </ProductDetails.Text>
          ) : null}

          {content.nutritionistPhoto && content.nutritionistTitle ? (
            <ProductDetails.Nutritionist
              photo={content.nutritionistPhoto}
              title={content.nutritionistTitle}
              logo={content.nutritionistLogo}
              logoWidth={content.nutritionistLogoWidth}
              logoHeight={content.nutritionistLogoHeight}
              foregroundColor={content.mobileHeaderForegroundColor}
            />
          ) : null}
        </ProductDetails.Header.Small.OuterContainer>

        {content.ingredientHighlights.length > 0 ? (
          <ProductDetails.ContentBlock withBorder={hasContentBelowIngredients}>
            <ProductDetails.SectionTitle>
              {content.ingredientHighlightsTitle ?? (
                <FormattedMessage {...messages.ingredientHighlightsTitle} />
              )}
            </ProductDetails.SectionTitle>

            <ProductDetails.IngredientsStack>
              {content.ingredientHighlights.map((entry) => (
                <ProductDetails.Ingredient.Container key={entry.name}>
                  <ProductDetails.Ingredient.Image
                    alt={entry.name}
                    baseUrl={entry.image.fields.file.url}
                  />

                  <ProductDetails.Ingredient.ContentContainer>
                    <ProductDetails.Ingredient.Title>{entry.name}</ProductDetails.Ingredient.Title>

                    <ProductDetails.Ingredient.Highlights highlights={entry.highlights} />
                  </ProductDetails.Ingredient.ContentContainer>
                </ProductDetails.Ingredient.Container>
              ))}
            </ProductDetails.IngredientsStack>
          </ProductDetails.ContentBlock>
        ) : null}

        {hasNutritionInfo ? (
          <ProductDetails.ContentBlock>
            <ProductDetails.SectionTitle>
              {content.nutritionInfoTitle ?? <FormattedMessage {...messages.nutritionInfoTitle} />}
            </ProductDetails.SectionTitle>

            <ProductNutritionData baseProduct={baseProduct} />
          </ProductDetails.ContentBlock>
        ) : null}

        {isUnavailableProduct ? (
          <ProductDetails.ContentBlock>
            <ProductDetails.UnavailableProduct />
          </ProductDetails.ContentBlock>
        ) : null}
      </Animated.ScrollView>

      <FloatingBar>
        <FloatingControls
          isLiquidGlassEnabled={isLiquidGlassEnabled}
          isFullWidthCta
          onBackButtonPress={navigateBack}
        >
          <Navbar.Button
            isLiquidGlassEnabled={isLiquidGlassEnabled}
            title={formatMessage(ctaMessage)}
            disabled={isAddingToBag}
            style={styles.navbarButton}
            onPress={handleCtaPress}
          />
        </FloatingControls>
      </FloatingBar>
    </FadeView>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getCtaMessage(params: CtaMessageParams) {
  const { canAddToBag, hasNoSelectedLocation } = params;

  if (canAddToBag) {
    return messages.addToBag;
  }

  if (hasNoSelectedLocation) {
    return messages.selectLocation;
  }

  return messages.changeLocation;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
  },
  navbarButton: {
    maxWidth: '100%',
  },
  contentContainer: {
    paddingBottom: Platform.select({
      android: theme.spacing['35'],
      default: theme.spacing['25'],
    }),
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  baseProduct: BaseProduct;
  content: ProductDetailsMappedContent;
  hasNoSelectedLocation: boolean;
  isUnavailableProduct: boolean;
  isAddingToBag: boolean;
  navigateBack: () => void;
  navigateToCustomization: () => void;
  navigateToLocationSearch: () => void;
};

type CtaMessageParams = {
  canAddToBag: boolean;
  hasNoSelectedLocation: boolean;
};
